/**
 * Pure credit-card math, extracted verbatim from `CardCalculator.tsx` so it can
 * be unit-tested without React.
 *
 * This is a *characterization* extraction: the loop below is the shipped
 * behaviour, copied as-is. The accompanying tests pin what the component
 * currently produces — they document today's arithmetic, they do not judge the
 * finance. Different formula family from `financing.ts` (revolving compounding
 * balance vs. flat-rate installment), hence a sibling module.
 */

/** Minimum payment floor in RM, and the fraction of the balance above it. */
const DEFAULT_MIN_PAYMENT_FLOOR = 50
const DEFAULT_MIN_PAYMENT_PCT = 0.05

/**
 * Monthly interest is capped at this fraction of that month's minimum payment,
 * so a balance can never grow faster than it is being paid down.
 */
const INTEREST_CAP_RATIO = 0.3

/** Upper bound on loop iterations, guarding against odd inputs never converging. */
const DEFAULT_SAFETY_LIMIT = 10000

export type CardPayoff = {
  /** Total profit/interest paid across the whole payoff. */
  totalInterest: number
  /** Number of monthly payments required. */
  months: number
  /** Minimum payment for month one, derived from the STARTING balance. */
  firstMonthMinPayment: number
}

/**
 * Cashback earned on a month's spend: `ratePercent` % of `spend`, optionally
 * capped. An uncapped call passes no `cap`.
 */
export function calculateCashback(spend: number, ratePercent: number, cap?: number): number {
  const earned = (spend * ratePercent) / 100
  return cap === undefined ? earned : Math.min(earned, cap)
}

/**
 * The card's minimum monthly payment: a percentage of the balance, floored at a
 * fixed minimum.
 */
export function calculateMinCardPayment(
  balance: number,
  floor: number = DEFAULT_MIN_PAYMENT_FLOOR,
  pct: number = DEFAULT_MIN_PAYMENT_PCT
): number {
  return Math.max(pct * balance, floor)
}

/**
 * Simulate paying a revolving balance down to zero, recomputing the minimum
 * payment from the remaining balance every month.
 *
 * @param startingBalance   Opening balance in RM.
 * @param aprPercent        Annual rate as a percentage (`18` means 18%).
 * @param additionalMonthlyPayment Extra paid on top of the minimum each month.
 */
export function simulateCardPayoff(
  startingBalance: number,
  aprPercent: number,
  additionalMonthlyPayment: number,
  options?: { safetyLimit?: number }
): CardPayoff {
  const balance = Number(startingBalance) || 0
  const apr = Number(aprPercent) || 0
  const addPay = Number(additionalMonthlyPayment) || 0
  const safetyLimit = options?.safetyLimit ?? DEFAULT_SAFETY_LIMIT

  if (balance <= 0 || apr < 0) {
    return { totalInterest: 0, months: 0, firstMonthMinPayment: 0 }
  }

  const monthlyRate = apr / 100 / 12

  let remaining = balance
  let months = 0
  let totalInterest = 0
  let safety = 0

  while (remaining > 0 && safety < safetyLimit) {
    safety += 1
    months += 1

    // Recalculate the minimum payment each month from the CURRENT balance.
    let minPayment = calculateMinCardPayment(remaining)
    if (addPay > 0) {
      minPayment += addPay
    }

    // The interest cap is 30% of THAT month's minimum payment.
    const interestCap = minPayment * INTEREST_CAP_RATIO
    const interest = Math.min(remaining * monthlyRate, interestCap)

    // Final month: pay exactly what is due, never more.
    const dueThisMonth = remaining + interest
    const payment = Math.min(minPayment, dueThisMonth)

    remaining = remaining - (payment - interest)
    totalInterest += interest

    // Clean up residual penny rounding.
    if (remaining < 0.005) remaining = 0
  }

  return {
    totalInterest,
    months,
    // Deliberately from the STARTING balance — this is what the UI labels as
    // the minimum monthly payment.
    firstMonthMinPayment: calculateMinCardPayment(balance)
  }
}
